import ArchiveIcon from "@redotech/redo-icons/archive.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import CornerUpLeftDoubleIcon from "@redotech/redo-icons/corner-up-left-double.svg";
import EyeOffIcon from "@redotech/redo-icons/eye-off_filled.svg";
import EyeIcon from "@redotech/redo-icons/eye.svg";
import FlagIcon from "@redotech/redo-icons/flag-01.svg";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward.svg";
import FlipForwardIcon from "@redotech/redo-icons/flip-forward.svg";
import GitMergeIcon from "@redotech/redo-icons/git-merge_filled.svg";
import RefreshCwIcon from "@redotech/redo-icons/refresh-cw-01.svg";
import SendIcon from "@redotech/redo-icons/send-01.svg";
import StickerSquareIcon from "@redotech/redo-icons/sticker-square.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import UserIcon from "@redotech/redo-icons/user-01.svg";
import type { ComponentType } from "react";
import type { KeyboardShortcut } from "../../keyboard-shortcuts/keyboard-shortcut";
import {
  conversationActionShortcuts,
  messageInputShortcuts,
  priorityShortcuts,
  snoozeShortcuts,
  tableNavigationShortcuts,
} from "../../keyboard-shortcuts/shopper-ai-shortcuts";
import { conversationActionIds, type ConversationActionId } from "./action-ids";

/**
 * Static action definition - UI metadata only.
 * Does not include isEnabled/isVisible which depend on domain context.
 */
export interface StaticActionDefinition {
  id: ConversationActionId;
  title: string;
  description: string;
  shortcut?: KeyboardShortcut;
  Icon?: ComponentType<{ className?: string }>;
  category?: string;
}

/**
 * All conversation action definitions with static metadata.
 * Domain-specific logic (isEnabled, isVisible) is added in support-v2.
 */
export const conversationActionDefinitions: Record<
  ConversationActionId,
  StaticActionDefinition
> = {
  // Table navigation
  [conversationActionIds.navigateUp]: {
    id: conversationActionIds.navigateUp,
    title: "Navigate Up",
    description: "Move selection up in the list",
    shortcut: tableNavigationShortcuts.NAVIGATE_UP,
  },
  [conversationActionIds.navigateDown]: {
    id: conversationActionIds.navigateDown,
    title: "Navigate Down",
    description: "Move selection down in the list",
    shortcut: tableNavigationShortcuts.NAVIGATE_DOWN,
  },
  [conversationActionIds.openConversation]: {
    id: conversationActionIds.openConversation,
    title: "Open",
    description: "Open the selected conversation",
    shortcut: tableNavigationShortcuts.OPEN_CONVERSATION,
  },
  [conversationActionIds.openActionMenu]: {
    id: conversationActionIds.openActionMenu,
    title: "Actions",
    description: "Open the action menu for the selected conversation",
    shortcut: tableNavigationShortcuts.OPEN_ACTION_MENU,
  },

  // Direct conversation actions
  [conversationActionIds.close]: {
    id: conversationActionIds.close,
    title: "Close",
    description: "Mark the conversation as closed",
    shortcut: conversationActionShortcuts.CLOSE,
    Icon: CheckCircleIcon,
  },
  [conversationActionIds.reopen]: {
    id: conversationActionIds.reopen,
    title: "Reopen",
    description: "Reopen the conversation",
    shortcut: conversationActionShortcuts.REOPEN,
    Icon: RefreshCwIcon,
  },
  [conversationActionIds.snooze]: {
    id: conversationActionIds.snooze,
    title: "Snooze",
    description: "Snooze the conversation until later",
    shortcut: conversationActionShortcuts.SNOOZE,
    Icon: ClockIcon,
  },
  [conversationActionIds.archive]: {
    id: conversationActionIds.archive,
    title: "Archive",
    description: "Archive the conversation",
    shortcut: conversationActionShortcuts.ARCHIVE,
    Icon: ArchiveIcon,
  },
  [conversationActionIds.delete]: {
    id: conversationActionIds.delete,
    title: "Delete",
    description: "Delete the conversation",
    shortcut: conversationActionShortcuts.DELETE,
    Icon: TrashIcon,
  },
  [conversationActionIds.markRead]: {
    id: conversationActionIds.markRead,
    title: "Mark as read",
    description: "Mark the conversation as read",
    shortcut: conversationActionShortcuts.MARK_READ,
    Icon: EyeIcon,
  },
  [conversationActionIds.markUnread]: {
    id: conversationActionIds.markUnread,
    title: "Mark as unread",
    description: "Mark the conversation as unread",
    shortcut: conversationActionShortcuts.MARK_UNREAD,
    Icon: EyeOffIcon,
  },

  // Submenu trigger actions
  [conversationActionIds.setPriority]: {
    id: conversationActionIds.setPriority,
    title: "Set priority",
    description: "Set the priority of the conversation",
    shortcut: conversationActionShortcuts.SET_PRIORITY,
    Icon: FlagIcon,
  },
  [conversationActionIds.assign]: {
    id: conversationActionIds.assign,
    title: "Assign",
    description: "Assign the conversation to a team member",
    shortcut: conversationActionShortcuts.ASSIGN,
    Icon: UserIcon,
  },
  [conversationActionIds.addTag]: {
    id: conversationActionIds.addTag,
    title: "Add tag",
    description: "Add tags to the conversation",
    shortcut: conversationActionShortcuts.ADD_TAG,
    Icon: TagIcon,
  },

  // Priority options
  [conversationActionIds.priorityUrgent]: {
    id: conversationActionIds.priorityUrgent,
    title: "Urgent",
    description: "Set priority to urgent",
    shortcut: priorityShortcuts.PRIORITY_URGENT,
  },
  [conversationActionIds.priorityClear]: {
    id: conversationActionIds.priorityClear,
    title: "Clear priority",
    description: "Remove priority from the conversation",
    shortcut: priorityShortcuts.PRIORITY_CLEAR,
  },

  // Snooze options
  [conversationActionIds.snooze1h]: {
    id: conversationActionIds.snooze1h,
    title: "1 hour",
    description: "Snooze for 1 hour",
    shortcut: snoozeShortcuts.SNOOZE_1H,
  },
  [conversationActionIds.snooze4h]: {
    id: conversationActionIds.snooze4h,
    title: "4 hours",
    description: "Snooze for 4 hours",
    shortcut: snoozeShortcuts.SNOOZE_4H,
  },
  [conversationActionIds.snooze24h]: {
    id: conversationActionIds.snooze24h,
    title: "24 hours",
    description: "Snooze for 24 hours",
    shortcut: snoozeShortcuts.SNOOZE_24H,
  },
  [conversationActionIds.snoozeCustom]: {
    id: conversationActionIds.snoozeCustom,
    title: "Custom...",
    description: "Set a custom snooze time",
    shortcut: snoozeShortcuts.SNOOZE_CUSTOM,
  },
  [conversationActionIds.snoozeClear]: {
    id: conversationActionIds.snoozeClear,
    title: "Clear Snooze",
    description: "Clear the snooze setting",
    shortcut: snoozeShortcuts.CLEAR_SNOOZE,
  },

  // Merge action
  [conversationActionIds.mergeTicket]: {
    id: conversationActionIds.mergeTicket,
    title: "Merge",
    description: "Merge this ticket with another",
    Icon: GitMergeIcon,
    shortcut: conversationActionShortcuts.MERGE,
  },

  // Message input actions
  [conversationActionIds.sendMessage]: {
    id: conversationActionIds.sendMessage,
    title: "Send",
    description: "Send the message",
    shortcut: messageInputShortcuts.SEND_MESSAGE,
    Icon: SendIcon,
  },
  [conversationActionIds.deleteDraft]: {
    id: conversationActionIds.deleteDraft,
    title: "Delete Draft",
    description: "Delete the current draft",
    shortcut: messageInputShortcuts.DELETE_DRAFT,
    Icon: TrashIcon,
  },

  // Reply mode actions
  [conversationActionIds.reply]: {
    id: conversationActionIds.reply,
    title: "Reply",
    description: "Reply to the sender",
    Icon: FlipBackwardIcon,
  },
  [conversationActionIds.replyAll]: {
    id: conversationActionIds.replyAll,
    title: "Reply all",
    description: "Reply to all recipients",
    Icon: CornerUpLeftDoubleIcon,
  },
  [conversationActionIds.forward]: {
    id: conversationActionIds.forward,
    title: "Forward",
    description: "Forward the message",
    Icon: FlipForwardIcon,
  },
  [conversationActionIds.internalNote]: {
    id: conversationActionIds.internalNote,
    title: "Internal note",
    description: "Add an internal note visible only to your team",
    Icon: StickerSquareIcon,
  },
};

/**
 * Get a static action definition by ID.
 */
export function getStaticActionDefinition(
  id: ConversationActionId,
): StaticActionDefinition {
  return conversationActionDefinitions[id];
}
